import React, { useEffect, useRef, useState } from 'react';
import { DatePicker } from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { parse, format, isValid } from 'date-fns';
import { isMaskedDob, MASKED_DOB_DISPLAY } from '../utils/formatters';

const DateInput = ({
  value,
  onChange,
  onRequestReveal = null,
  placeholder = "mm-dd-yyyy",
  className = "w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500",
  disabled = false,
  error = false,
  minDate = undefined,
  maxDate = undefined,
  ...rest
}) => {
  const datePickerRef = useRef(null);
  const masked = isMaskedDob(value);
  const revealGated = typeof onRequestReveal === 'function';
  const [open, setOpen] = useState(false);
  const [pendingOpen, setPendingOpen] = useState(false);
  // Parse incoming YYYY-MM-DD string to Date object
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const parsed = parse(dateStr, 'yyyy-MM-dd', new Date());
    return isValid(parsed) ? parsed : null;
  };

  const selectedDate = masked ? null : parseDate(value);
  const openToDate = (() => {
    if (!selectedDate) return undefined;
    if (minDate && selectedDate < minDate) return minDate;
    if (maxDate && selectedDate > maxDate) return maxDate;
    return selectedDate;
  })();

  useEffect(() => {
    if (pendingOpen && !masked) {
      setOpen(true);
      setPendingOpen(false);
    }
  }, [pendingOpen, masked]);

  const requestOpen = () => {
    if (disabled) return;
    if (masked && revealGated) {
      if (pendingOpen) return;
      setPendingOpen(true);
      Promise.resolve(onRequestReveal()).then((revealed) => {
        if (revealed === null || revealed === undefined) setPendingOpen(false);
      });
      return;
    }
    setOpen(true);
  };

  const openUncontrolled = () => {
    if (!disabled && datePickerRef.current) {
      datePickerRef.current.setFocus();
      datePickerRef.current.setOpen(true);
    }
  };

  const handleDateChange = (date) => {
    if (revealGated) setOpen(false);
    if (date) {
      onChange(format(date, 'yyyy-MM-dd'));
    } else {
      onChange('');
    }
  };

  const baseClassName = className;
  const errorClass = "bg-red-50/30 border-red-300 focus:ring-red-500 focus:border-red-500";
  const finalClassName = (error && className.includes('border-gray-300')
    ? baseClassName.replace('border-gray-300', errorClass).replace('focus:ring-emerald-500 focus:border-emerald-500', '')
    : baseClassName) + (!className.includes('pr-10') ? ' pr-10' : '');

  return (
    <div className="relative w-full group">
      <DatePicker
        ref={datePickerRef}
        selected={selectedDate}
        onChange={handleDateChange}
        onChangeRaw={(e) => e.preventDefault()}
        openToDate={openToDate}
        {...(revealGated
          ? {
              open,
              onInputClick: requestOpen,
              onClickOutside: () => setOpen(false),
              onCalendarClose: () => setOpen(false),
            }
          : {})}
        dateFormat="MM-dd-yyyy"
        placeholderText={masked ? "" : placeholder}
        disabled={disabled}
        className={finalClassName}
        wrapperClassName="w-full"
        showYearDropdown
        showMonthDropdown
        scrollableYearDropdown
        yearDropdownItemNumber={100}
        dropdownMode="select"
        popperPlacement="bottom-start"
        portalId="root-portal"
        minDate={minDate}
        maxDate={maxDate}
        {...rest}
      />
      {masked && (
        <div
          className={`absolute inset-y-0 left-0 right-10 px-3 flex items-center text-sm pointer-events-none tracking-widest ${disabled ? 'text-gray-400' : 'text-gray-900'}`}
          aria-hidden="true"
        >
          {MASKED_DOB_DISPLAY}
        </div>
      )}
      <div
        className={`absolute top-1/2 right-2 -translate-y-1/2 p-1 rounded transition-colors flex items-center justify-center ${disabled ? 'text-gray-400 cursor-not-allowed' : 'text-emerald-600 hover:bg-emerald-50 cursor-pointer'}`}
        onClick={revealGated ? requestOpen : openUncontrolled}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </div>
    </div>
  );
};

export default DateInput;
